import { AgDialog } from '@australiangreens/ag-internal-components';
import { Menu, MenuItem } from '@mui/material';
import { Dispatch, useState } from 'react';

export type CloseOutcome = 'cancel' | 'setIsTemplatePlaceholder' | 'unsetIsTemplatePlaceholder';

/**
 * If null, indicates the menu is closed
 */
export type ContextMenuState = {
  mouseX: number;
  mouseY: number;
} | null;

export type TemplatePlaceholderContextMenuProps = {
  menuState: ContextMenuState;
  onClose: (outcome: CloseOutcome) => void;
  placeholderActive: boolean;
};

export default function TemplatePlaceholderContextMenu({
  menuState,
  onClose,
  placeholderActive,
}: TemplatePlaceholderContextMenuProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleRemove = async () => {
    setIsDialogOpen(false);
    onClose('unsetIsTemplatePlaceholder');
  };

  return (
    <>
      <Menu
        open={menuState !== null}
        anchorReference="anchorPosition"
        anchorPosition={
          menuState !== null ? { top: menuState.mouseY, left: menuState.mouseX } : undefined
        }
        onClose={(_event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            onClose('cancel');
          }
        }}
      >
        <MenuItem
          disabled={placeholderActive}
          onClick={() => {
            onClose('setIsTemplatePlaceholder');
          }}
        >
          Set as placeholder
        </MenuItem>

        {/* Future task */}
        <MenuItem sx={{ display: 'none' }} disabled>
          Combine with existing placeholder
        </MenuItem>

        <MenuItem
          disabled={!placeholderActive}
          onClick={() => {
            setIsDialogOpen(true);
          }}
        >
          Remove placeholder
        </MenuItem>
      </Menu>
      <AgDialog
        dialogTitle="Remove placeholder"
        isOpen={isDialogOpen}
        primaryButton={{
          text: 'Remove',
          onClick: handleRemove,
        }}
        onClose={() => {
          setIsDialogOpen(false);
          onClose('cancel');
        }}
        secondaryButton={{ text: 'Cancel' }}
      >
        Are your sure you want to remove this placeholder?
        <br />
        <br />
        <em>Note: any text you have added to the instructions field will be lost.</em>
      </AgDialog>
    </>
  );
}

/**
 * A helper function that properly opens the placeholder context menu in the
 * correct location.
 *
 * Use in the right click event.
 */
export function handleOpenPlaceholderContextMenu(
  event: React.MouseEvent,
  menuState: ContextMenuState,
  setMenuState: Dispatch<React.SetStateAction<ContextMenuState>>
) {
  setMenuState(
    menuState === null
      ? {
          mouseX: event.clientX,
          mouseY: event.clientY,
        }
      : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
        // Other native context menus might behave different.
        // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
        null
  );

  // Prevent text selection lost after opening the context menu on Safari and Firefox
  const selection = document.getSelection();
  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);

    setTimeout(() => {
      selection.addRange(range);
    });
  }
}
